// src/modules/wiring/hooks/useWireSplitMenu.js
//
// Armatür (fixture) hattına sağ tıklanınca "Böl" menüsünü açar.
// Kullanıcı toplam armatür sayısını seçer; ilk ve son armatür arasına
// eşit aralıklarla yeni armatürler eklenir.

import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import WireSplitMenu from '../../../components/WireSplitMenu/WireSplitMenu';
import { createLightFixture } from '../../../factory/symbols/createLightFixtureSymbols';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { updateComponent } from '../../../redux/simpleSupplySlice';
import { incrementUpdateCounter } from '../../../redux/utilsSlice';
import { store as appStore } from '../../../redux/store';

// ---------- Geometri yardımcıları ----------

function computeCumLengths(vertices) {
  const cumLengths = [0];
  for (let i = 1; i < vertices.length; i++) {
    cumLengths.push(cumLengths[i - 1] + vertices[i - 1].distanceTo(vertices[i]));
  }
  return { cumLengths, total: cumLengths[cumLengths.length - 1] };
}

/** Wire üzerinde t parametresine (0-1) karşılık gelen pozisyonu döner. */
function getPositionAtParam(vertices, cumLengths, total, t) {
  const target = t * total;
  for (let i = 1; i < vertices.length; i++) {
    if (cumLengths[i] >= target - 1e-9) {
      const segLen = cumLengths[i] - cumLengths[i - 1];
      const segT = segLen > 0 ? (target - cumLengths[i - 1]) / segLen : 0;
      return new THREE.Vector3().lerpVectors(
        vertices[i - 1], vertices[i], Math.max(0, Math.min(1, segT))
      );
    }
  }
  return vertices[vertices.length - 1].clone();
}

/** Wire üzerinde t parametresine karşılık gelen yön açısını (radyan) döner. */
function getDirectionAngleAtParam(vertices, cumLengths, total, t) {
  const target = t * total;
  for (let i = 1; i < vertices.length; i++) {
    if (cumLengths[i] >= target - 1e-9) {
      const dir = new THREE.Vector3().subVectors(vertices[i], vertices[i - 1]);
      return Math.atan2(dir.y, dir.x);
    }
  }
  const last = vertices.length - 1;
  const dir = new THREE.Vector3().subVectors(vertices[last], vertices[last - 1]);
  return Math.atan2(dir.y, dir.x);
}

/** Verilen 3D noktanın wire üzerindeki konumunu t (0-1) olarak döner. */
function getParamAtPoint(vertices, cumLengths, total, point) {
  if (total < 1e-9) return 0;
  let bestT = 0, bestDist = Infinity;
  for (let i = 1; i < vertices.length; i++) {
    const a = vertices[i - 1];
    const b = vertices[i];
    const ab = new THREE.Vector3().subVectors(b, a);
    const ap = new THREE.Vector3().subVectors(point, a);
    const lenSq = ab.lengthSq();
    const segT = lenSq > 0 ? Math.max(0, Math.min(1, ap.dot(ab) / lenSq)) : 0;
    const projected = new THREE.Vector3().lerpVectors(a, b, segT);
    const d = point.distanceTo(projected);
    if (d < bestDist) {
      bestDist = d;
      bestT = (cumLengths[i - 1] + segT * (cumLengths[i] - cumLengths[i - 1])) / total;
    }
  }
  return bestT;
}

/**
 * Wire vertex'lerini DÜNYA koordinatında okur. Armatür hattı mesh-FP sweep'iyle
 * fpMesh çocuğu olabilir → geometry LOCAL kalır, matrixWorld uygulanmazsa armatür
 * dünya konumlarıyla uyuşmaz (kat planı taşınınca/reload sonrası bozulurdu).
 */
function getWireWorldVertices(wire) {
  const posAttr = wire.geometry.attributes.position;
  wire.updateMatrixWorld(true);
  const out = [];
  for (let i = 0; i < posAttr.count; i++) {
    out.push(
      new THREE.Vector3(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i))
        .applyMatrix4(wire.matrixWorld)
    );
  }
  return out;
}

/** Wire vertex'lerine yakın LightFixture nesnelerini bulur (armatürler vertex'te oturur). */
function findFixturesOnWire(scene, vertices) {
  const out = [];
  const wpos = new THREE.Vector3();
  scene.traverse(obj => {
    if (obj.userData?.type !== 'LightFixture') return;
    obj.getWorldPosition(wpos);
    let minDist = Infinity;
    for (const v of vertices) {
      const d = wpos.distanceTo(v);
      if (d < minDist) minDist = d;
    }
    if (minDist < 8) out.push({ obj, pos: wpos.clone() });
  });
  return out;
}

// ---------- Plan + Bölme mantığı ----------

/**
 * Wire üzerindeki mevcut armatürleri bulur ve totalCount'a göre eklenecek yeni
 * armatürlerin konum/açılarını hesaplar. Hem önizleme hem kalıcı yerleştirme kullanır.
 */
function computeFixturePlan(scene, wire, totalCount) {
  const vertices = getWireWorldVertices(wire);
  if (vertices.length < 2) return null;

  const { cumLengths, total } = computeCumLengths(vertices);
  if (total < 1e-9) return null;

  const found = findFixturesOnWire(scene, vertices);
  if (found.length < 2) return null;

  const withParams = found.map(({ obj, pos }) => ({
    obj,
    t: getParamAtPoint(vertices, cumLengths, total, pos),
  }));
  withParams.sort((a, b) => a.t - b.t);

  const firstT = withParams[0].t;
  const lastT = withParams[withParams.length - 1].t;
  if (lastT - firstT < 1e-6) return null;

  const ref = withParams[0].obj;
  const fixtureType = ref.userData?.fixtureType || ref.userData?.symbol || 'normal';
  const power = ref.userData?.power || 10;
  const color = wire.material?.color?.getHex?.() ?? 0x0045ff;
  const loadId = wire.userData?.parentLightingLoadId || wire.userData?.lightingLoadId || null;

  const toAdd = Math.max(0, totalCount - found.length);
  const placements = [];
  for (let i = 1; i <= toAdd; i++) {
    const t = firstT + i * (lastT - firstT) / (toAdd + 1);
    placements.push({
      position: getPositionAtParam(vertices, cumLengths, total, t),
      angle: getDirectionAngleAtParam(vertices, cumLengths, total, t),
    });
  }
  return { existingCount: found.length, fixtureType, power, color, loadId, placements };
}

/** Tek armatür sembolü oluşturur (kalıcı ya da yarı-saydam önizleme). */
function makeFixture(position, angle, color, fixtureType, power, isPreview, id) {
  const group = createLightFixture(null, position, 1, color, color, isPreview, fixtureType, null, 0, id);
  group.rotation.z = angle;
  group.userData = {
    ...group.userData,
    id,
    type: isPreview ? 'LightFixturePreview' : 'LightFixture', // preview: findFixturesOnWire sayMASIN
    isPreview,
    isSelectable: !isPreview,
    fixtureType,
    symbol: fixtureType,
    power,
    originalColor: color,
  };
  if (isPreview) {
    group.traverse(c => {
      if (c.material) { c.material.transparent = true; c.material.opacity = 0.45; }
    });
  }
  return group;
}

function splitFixtureWire(scene, dispatch, wire, totalCount) {
  const plan = computeFixturePlan(scene, wire, totalCount);
  if (!plan || plan.placements.length === 0) return;

  const { fixtureType, power, color, loadId, placements } = plan;
  const newFixtures = [];
  for (const { position, angle } of placements) {
    const newId = generateUniqueId('fixture');
    const group = makeFixture(position, angle, color, fixtureType, power, false, newId);
    scene.add(group);
    newFixtures.push({
      id: newId,
      type: fixtureType,
      position: { x: position.x, y: position.y, z: 0 },
      power,
      uuid: group.uuid,
    });
  }

  // Load güç + armatür listesini güncelle
  if (loadId) {
    const load = appStore.getState().simpleSupply.components.find(c => c.id === loadId);
    if (load) {
      const addedPower = newFixtures.reduce((s, f) => s + (f.power || 0), 0);
      const newInstalled = (load.electrical?.installedPower || 0) + addedPower;
      dispatch(updateComponent({
        id: loadId,
        changes: {
          electrical: {
            installedPower: newInstalled,
            demandPower: (load.electrical?.demandPower || 0) + addedPower,
            current: newInstalled / 220,
          },
          metadata: {
            fixtures: [...(load.metadata?.fixtures || []), ...newFixtures],
          },
        },
      }));
    }
  }

  dispatch(incrementUpdateCounter());
}

// ---------- Hook ----------

export default function useWireSplitMenu(scene, camera, renderer) {
  const dispatch = useDispatch();
  const commandType = useSelector(state => state.operation.commandType);
  const commandTypeRef = useRef(commandType);
  useEffect(() => { commandTypeRef.current = commandType; }, [commandType]);

  const [menu, setMenu] = useState(null); // { x, y } | null
  const wireRef = useRef(null);
  const existingCountRef = useRef(0);
  const previewObjsRef = useRef([]);

  // Önizleme hayalet armatürlerini sahneden temizle
  const clearPreview = () => {
    for (const obj of previewObjsRef.current) {
      obj.traverse?.(c => {
        if (c.geometry) c.geometry.dispose();
        if (c.material) {
          if (Array.isArray(c.material)) c.material.forEach(m => m.dispose());
          else c.material.dispose();
        }
      });
      if (obj.parent) obj.parent.remove(obj);
      else if (scene) scene.remove(obj);
    }
    previewObjsRef.current = [];
    dispatch(incrementUpdateCounter());
  };

  // n toplam armatür için yarı-saydam önizleme göster
  const showPreview = n => {
    clearPreview();
    const wire = wireRef.current;
    if (!wire || !scene || n == null) return;
    const plan = computeFixturePlan(scene, wire, n);
    if (!plan) return;
    for (const { position, angle } of plan.placements) {
      const g = makeFixture(position, angle, plan.color, plan.fixtureType, plan.power, true, generateUniqueId('fixturePreview'));
      scene.add(g);
      previewObjsRef.current.push(g);
    }
    dispatch(incrementUpdateCounter());
  };

  useEffect(() => {
    if (!renderer || !scene || !camera) return;
    const dom = renderer.domElement;

    const onClick = e => {
      if (commandTypeRef.current) return; // Çizim modundayken yoksay
      if (e.button !== 0) return; // Sadece sol tık

      const rect = dom.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      const raycaster = new THREE.Raycaster();
      raycaster.params.Line = { threshold: 5 };
      raycaster.setFromCamera(ndc, camera);

      // Sahneyi tara: sadece armatür hattı (fixture wire)
      const candidates = [];
      scene.traverse(obj => {
        if (obj.isLine && obj.userData?.function === 'wireLightingFixture') {
          candidates.push(obj);
        }
      });
      if (!candidates.length) return;

      const hits = raycaster.intersectObjects(candidates, false);
      if (!hits.length) return;

      const wire = hits[0].object;
      const vertices = getWireWorldVertices(wire);
      const fixtures = findFixturesOnWire(scene, vertices);
      if (fixtures.length < 2) return; // En az 2 armatür (ilk + son) gerekli

      e.preventDefault();
      e.stopPropagation();
      wireRef.current = wire;
      existingCountRef.current = fixtures.length;
      setMenu({ x: e.clientX, y: e.clientY });
    };

    dom.addEventListener('click', onClick);
    return () => dom.removeEventListener('click', onClick);
  }, [renderer, scene, camera]);

  // Menü dışı tıklama → kapat (+ önizleme temizle)
  useEffect(() => {
    if (!menu) return;
    const onDown = () => { clearPreview(); setMenu(null); };
    setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => document.removeEventListener('mousedown', onDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);

  // Unmount'ta (tab/scene değişimi) önizleme hayaletleri sahnede kalmasın
  useEffect(() => () => clearPreview(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeMenu = () => { clearPreview(); setMenu(null); };

  const handleSplit = totalCount => {
    const wire = wireRef.current;
    clearPreview();
    setMenu(null);
    if (!wire || !scene) return;
    splitFixtureWire(scene, dispatch, wire, totalCount);
  };

  if (!menu) return null;

  return createPortal(
    <WireSplitMenu
      x={menu.x}
      y={menu.y}
      existingCount={existingCountRef.current}
      onSplit={handleSplit}
      onPreview={showPreview}
      onClose={closeMenu}
    />,
    document.body,
  );
}
